/**
 * Turning a spreadsheet heading into something a template can bind to, without
 * losing what the heading said.
 *
 * Two outputs, and the reason there are two is worth stating: the expression
 * language's tokeniser accepts identifiers, so `Total Fees (£)` cannot be a
 * field name; but a document that prints `totalFeesGbp` as a table heading has
 * thrown away the only piece of typography the user actually supplied. So the
 * identifier is derived and the heading is kept verbatim as the label.
 */

/** Letters the tokeniser will not accept in an identifier, plus everything else. */
const NOT_IDENTIFIER = /[^A-Za-z0-9]+/g;

/**
 * `Total Fees (£)` → `totalFees`.
 *
 * Currency symbols, units and bracketed notes fall out because they are not
 * alphanumeric; what is left is camel-cased. A heading that reduces to nothing
 * at all — `£`, `#`, `(%)` — returns an empty string, and the caller numbers it
 * instead, because `column3` is more use than a field with no name.
 */
export function identifier(header: string): string {
  const words = header
    .replace(/[_.-]+/g, " ")
    // Split runs like `TotalFees` and `VATRate` so the camel-casing below has
    // real words to work with rather than one long one.
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .split(NOT_IDENTIFIER)
    .filter(Boolean);
  if (!words.length) return "";
  const [first, ...rest] = words as [string, ...string[]];
  const head = /^[0-9]/.test(first) ? `n${first}` : first.toLowerCase();
  return head + rest.map((w) => w[0]!.toUpperCase() + w.slice(1).toLowerCase()).join("");
}

/**
 * A label for a column whose heading was missing or unusable.
 *
 * "Column 3" and not "column3": it goes in front of a person, and a synthesised
 * name should look synthesised rather than look like a field somebody chose.
 */
export function placeholderLabel(index: number): string {
  return `Column ${index + 1}`;
}

/**
 * Makes a list of identifiers unique, keeping the first of each.
 *
 * Spreadsheets duplicate headings constantly — two columns called `Amount`, or
 * three blank ones — and a data source with two fields of the same name loses
 * one of them silently.
 */
export function unique(names: string[]): string[] {
  const seen = new Map<string, number>();
  return names.map((name) => {
    const n = seen.get(name) ?? 0;
    seen.set(name, n + 1);
    return n === 0 ? name : `${name}${n + 1}`;
  });
}

/**
 * Words that are capitals in a business document whatever the case around them.
 *
 * A list rather than a rule about short words, because a rule that keeps `VAT`
 * also keeps `NET` and `DUE`, and "NET amount" is as wrong as "Vat".
 */
const ACRONYMS = new Set([
  "VAT", "GST", "HST", "PST", "SKU", "EAN", "UPC", "ISBN", "IBAN", "BIC", "ID", "PO", "UK", "US", "USA", "EU",
  "GBP", "USD", "EUR", "NI", "NHS", "UTR", "EIN", "ABN", "YTD", "MTD", "QTD", "FY", "KPI", "ROI", "URL", "PDF"
]);

/**
 * A heading tidied for display, when the original is shouting or snake-cased.
 *
 * `TOTAL_FEES` → `Total fees`; `clientName` → `Client name`. A heading that is
 * already sentence case is left exactly as it is, because the user wrote it
 * that way on purpose and second-guessing them is how `iPhone Sales` becomes
 * `Iphone sales`. An acronym stays one: `VAT 20%` → `VAT 20%`, not `Vat 20%` — found
 * as the heading of a real invoice's tax column.
 */
export function tidyLabel(header: string): string {
  const trimmed = header.trim();
  if (!trimmed) return "";
  const looksShouted = trimmed === trimmed.toUpperCase() && /[A-Z]{2,}/.test(trimmed);
  // A dot separates as well as an underscore does: a flattened JSON key arrives
  // here as `client.name`, and left alone it becomes a document heading that
  // reads like a field path.
  const looksSnaked = /[_.]/.test(trimmed) && !/\s/.test(trimmed);
  const looksCamel = /^[a-z]+[A-Z]/.test(trimmed) && !/\s/.test(trimmed);
  if (!looksShouted && !looksSnaked && !looksCamel) return trimmed;
  const words = trimmed
    .replace(/[_.]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (ACRONYMS.has(w.replace(/[^A-Za-z]/g, "").toUpperCase()) ? w.toUpperCase() : w.toLowerCase()));
  if (!words.length) return trimmed;
  return words[0]![0]!.toUpperCase() + words[0]!.slice(1) + (words.length > 1 ? ` ${words.slice(1).join(" ")}` : "");
}
